// pipe (ROMS+Monday P0.1): pure client-side export helpers for a finished report run — CSV
// ride-along + XLSX via the SAME lazy `import('exceljs')` chunk pattern the document viewer
// already ships (the read path; this is the write twin — the run/typing path never pays for the
// library). Pure module: no React, no services — unit-testable under node/happy-dom.
//
// Redaction safety is by construction: everything exported here is the ALREADY column-gated,
// partition-scoped run result the server emitted — this module never widens anything.

/** The one column property the exports read. */
export interface ReportExportColumn {
  key: string;
}

/**
 * The minimal structural slice of a finished report run these builders consume. @affine/core's
 * `ReportRunResult` satisfies it (its extras — title, truncation flag — are not export inputs), so
 * core passes its service type straight through; this package never imports core's types.
 */
export interface ReportExportResult {
  key: string;
  columns: ReportExportColumn[];
  rows: unknown[][];
  footer_totals: Record<string, number>;
}

/** A cell value as the string the exports carry (null/undefined → empty). */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * One CSV field, formula-injection-neutralized (CWE-1236 — mirrors the server's shared
 * `csv::field` rule): a cell a spreadsheet would evaluate (leading `=`, `@`, TAB, CR, or a
 * leading `+`/`-` that is NOT a plain number) is prefixed with `'` so it opens as literal text,
 * while a plain negative number (a credit line's -100.00) passes through untouched. Quotes are
 * doubled and the field is quoted when it carries a delimiter/quote/newline.
 */
export function csvField(v: unknown): string {
  let s = cellText(v);
  const first = s.charAt(0);
  const dangerous =
    first === '=' ||
    first === '@' ||
    first === '\t' ||
    first === '\r' ||
    ((first === '+' || first === '-') &&
      s.length > 1 &&
      Number.isNaN(Number(s)));
  if (dangerous) s = `'${s}`;
  if (/[",\n\r]/.test(s)) s = `"${s.replaceAll('"', '""')}"`;
  return s;
}

/** The full CSV text of a run: localized header row, body rows, and (when present) the
 *  footer-totals row under its columns — the same shape the on-screen grid renders. */
export function toCsv(
  result: ReportExportResult,
  columnLabels: string[],
  totalsLabel: string
): string {
  const lines: string[] = [];
  lines.push(columnLabels.map(csvField).join(','));
  for (const row of result.rows) {
    lines.push(row.map(csvField).join(','));
  }
  const totals = footerRow(result, totalsLabel);
  if (totals) lines.push(totals.map(csvField).join(','));
  return `${lines.join('\r\n')}\r\n`;
}

/**
 * Which cell of {@link footerRow}'s output carries the row's LABEL rather than a total — `-1` when
 * column 0 holds a total of its own and the label has nowhere to go.
 *
 * Exported because the SCREEN needs it too: card mode captions every cell with its column's header,
 * so the label cell has to opt out or the totals card reads "Item Barcode — Totals". `footerRow`
 * places the label through this same predicate, so the two can never disagree (SSOT).
 */
export function footerLabelIndex(result: ReportExportResult): number {
  const first = result.columns[0];
  return first && typeof result.footer_totals[first.key] !== 'number' ? 0 : -1;
}

/** The footer-totals row aligned under the run's columns (null when the run has no totals).
 *  The first column carries the localized "Totals" label unless it itself holds a total. */
export function footerRow(
  result: ReportExportResult,
  totalsLabel: string
): (string | number)[] | null {
  const keys = Object.keys(result.footer_totals);
  if (keys.length === 0) return null;
  const row: (string | number)[] = result.columns.map(c => {
    const v = result.footer_totals[c.key];
    return typeof v === 'number' ? v : '';
  });
  const labelIndex = footerLabelIndex(result);
  if (labelIndex >= 0) row[labelIndex] = totalsLabel;
  return row;
}

/** Trigger a browser download of `bytes` under `filename`. */
export function downloadBlob(
  bytes: BlobPart,
  filename: string,
  mime: string
): void {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Build + download the XLSX (one sheet per run for now) via the lazy exceljs chunk. */
export async function downloadXlsx(
  result: ReportExportResult,
  columnLabels: string[],
  totalsLabel: string,
  filename: string
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  // Sheet names cap at 31 chars and reject \ / ? * [ ] : — sanitize the report key.
  const sheetName =
    result.key.replaceAll(/[\\/?*[\]:]/g, '_').slice(0, 31) || 'report';
  const ws = wb.addWorksheet(sheetName);
  ws.addRow(columnLabels);
  ws.getRow(1).font = { bold: true };
  for (const row of result.rows) {
    // Numbers stay numbers in the sheet; everything else exports as its rendered text (a
    // spreadsheet cell is typed, so no formula-injection prefix is needed here — exceljs writes
    // inline strings, never formulas).
    ws.addRow(row.map(v => (typeof v === 'number' ? v : cellText(v))));
  }
  const totals = footerRow(result, totalsLabel);
  if (totals) {
    const r = ws.addRow(totals);
    r.font = { bold: true };
  }
  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(
    buf,
    filename,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}
